/* UpgradeBanner.jsx — dismissible in-app upgrade card (web only).
 *
 * Starter -> Pro message; Pro -> Elite message; Elite -> nothing.
 * Behind UPGRADE_BANNER_ENABLED (config/upgradeBanner.js), OFF by default.
 * Never renders on the native iOS app (Guideline 3.1.1): the IS_NATIVE_APP
 * check is the first thing this component does, and the same rule is tested in
 * utils/upgradeBannerLogic.js.
 *
 * "Dismissed" is saved on the user's account (POST /account/upgrade-banner/
 * dismiss) so it follows them across devices, and mirrored into the cached
 * user so it closes instantly.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { accountApi } from '../services/api'
import { IS_NATIVE_APP } from '../services/runtime'
import { JAI_COLORS, JAI_FONTS } from './JaiBrand'
import { pickBannerVariant } from '../utils/upgradeBannerLogic'
import { UPGRADE_BANNER_ENABLED, bannerContent } from '../config/upgradeBanner'

export default function UpgradeBanner() {
  const { user, isGuest, plan, planLoading, updateUser } = useAuth()
  const [closing, setClosing] = useState(false)

  // Native: never. Checked before anything else, including the flag.
  if (IS_NATIVE_APP) return null
  if (planLoading) return null

  const variant = pickBannerVariant({
    enabled: UPGRADE_BANNER_ENABLED,
    isNative: IS_NATIVE_APP,
    signedIn: !!user,
    isGuest,
    plan,
    dismissedFor: user?.upgradeBannerDismissedFor || null,
  })
  if (!variant || closing) return null

  const { title, body, cta, to } = bannerContent(variant)

  const dismiss = () => {
    setClosing(true)
    updateUser({ upgradeBannerDismissedFor: variant })
    accountApi.dismissUpgradeBanner(variant).catch(() => { /* cached copy still hides it here */ })
  }

  return (
    <div role="region" aria-label={title}
      style={{
        margin: '12px 16px 0', padding: '2px 0 2px 12px', borderRadius: 0,
        borderLeft: `3px solid ${JAI_COLORS.blue}`,
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', fontFamily: JAI_FONTS.tight,
      }}>
      <div style={{ flex: '1 1 260px', minWidth: 0 }}>
        <div style={{ color: JAI_COLORS.navy, fontSize: 13.5, fontWeight: 700 }}>{title}</div>
        <div style={{ color: JAI_COLORS.mute, fontSize: 12.5, lineHeight: 1.5, marginTop: 2 }}>{body}</div>
      </div>
      <Link to={to}
        style={{ background: JAI_COLORS.blue, color: '#fff', textDecoration: 'none', borderRadius: 9, padding: '8px 14px', fontSize: 12.5, fontWeight: 650, whiteSpace: 'nowrap' }}>
        {cta}
      </Link>
      <button type="button" onClick={dismiss} aria-label="Dismiss"
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: JAI_COLORS.mute, fontSize: 20, lineHeight: 1, padding: '2px 6px' }}>
        ×
      </button>
    </div>
  )
}
